import React from "react";
import { ControlledModal } from "../../layouts/components/modal";
import { Teacher, useAppContext } from "../../providers/context";
import { makeRequest, showAxiosErrorAsToast } from "../../request";
import {
  Button,
  ButtonShape,
  ButtonStyle,
} from "../../components/simple/button";
import { LabeledCheckbox } from "../../components/form/checkbox";
import {
  getPresignedUrl,
  inputSingleFile,
  uploadToS3,
} from "../../components/media/fileUpload";
import { IconTimes } from "../../icons/library";

export const DistrictSettingsModal: React.FC<{
  user: Teacher;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext();
  const [loading, setLoading] = React.useState(false);
  const [showAllRecipients, setShowAllRecipients] = React.useState(
    user.district.showAllRecipients
  );
  const [showForwardingOptions, setShowForwardingOptions] = React.useState(
    user.district.showForwardingOptions
  );
  const [newLogoName, setNewLogoName] = React.useState<string | undefined>(
    undefined
  );
  const [logoUploading, setLogoUploading] = React.useState(false);
  return (
    <ControlledModal
      size="sm"
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      open={true}
      title={user.districtAccess ? "Edit District" : "Edit School"}
      disabled={loading}
    >
      <div className="flex flex-col gap-1">
        Should users see only other users in their classes or should they see
        everyone in their school? Admins are not affected.
        <LabeledCheckbox
          checked={showAllRecipients}
          onChecked={setShowAllRecipients}
          label="Show all users"
        />
      </div>
      <div className="mt-3 flex flex-col gap-1">
        Should teachers be able to disable forwarded message notifications
        <LabeledCheckbox
          checked={showForwardingOptions}
          onChecked={setShowForwardingOptions}
          label="Show forwarding options"
        />
      </div>
      <DistrictLogoUpload
        logoLink={user.district.logoLink}
        uploadDisabled={loading}
        logoUploading={logoUploading}
        setLogoUploading={setLogoUploading}
        setNewLogoName={setNewLogoName}
      />
      {newLogoName && (
        <div className="text-sm text-logo">
          <b>Press "Save"</b> to finish updating your logo to "{newLogoName}"
        </div>
      )}
      <Button
        logo
        disabled={loading || logoUploading}
        className="mt-4 self-start"
        onClick={() => {
          setLoading(true);
          makeRequest<void>("/district/edit", {
            showallrecipients: showAllRecipients,
            showforwardingoptions: showForwardingOptions,
            newLogoName,
          })
            .then(() => {
              reloadUser();
              onClose();
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false));
        }}
      >
        Save
      </Button>
    </ControlledModal>
  );
};

export const DistrictLogoUpload: React.FC<{
  logoLink?: string;
  uploadDisabled: boolean;
  logoUploading: boolean;
  setLogoUploading: React.Dispatch<React.SetStateAction<boolean>>;
  setNewLogoName: React.Dispatch<React.SetStateAction<string | undefined>>;
}> = ({
  logoLink,
  uploadDisabled,
  logoUploading,
  setLogoUploading,
  setNewLogoName,
}) => (
  <div className="mt-4 flex items-center justify-between gap-4">
    <div className="flex flex-col gap-2">
      <b>District Logo</b>
      <Button
        onClick={async () => {
          setLogoUploading(true);
          const file = await inputSingleFile(
            "image/png, image/jpeg, image/gif, image/jpg"
          );
          if (file) {
            try {
              const url = await getPresignedUrl(file.name);
              await uploadToS3(url, file);
              setNewLogoName(file.name);
            } catch (e) {
              showAxiosErrorAsToast(e);
            }
          }
          setLogoUploading(false);
        }}
        disabled={uploadDisabled || logoUploading}
        showLoader={logoUploading}
        className="self-start"
      >
        {logoUploading ? "Uploading" : logoLink ? "Change logo" : "Upload logo"}
      </Button>
    </div>
    {logoLink ? (
      <img
        src={logoLink}
        className="max-h-[50px] max-w-[300px] object-contain shadow-even"
        height="50px"
        alt="District logo"
      />
    ) : (
      <div className="flex h-[50px] w-[50px] items-center justify-center bg-slate-100 text-sm shadow-even">
        no logo
      </div>
    )}
    {logoLink && (
      <Button
        buttonStyle={ButtonStyle.ghostRedTransparent}
        shape={ButtonShape.icon}
        tooltip="Remove logo"
        onClick={() => {
          setNewLogoName("remove");
        }}
      >
        <IconTimes />
      </Button>
    )}
  </div>
);
